import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, DollarSign, Trash2, RotateCcw, Edit, Building2, User, MapPin, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { DndContext, DragOverlay, closestCorners, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Lead Card Component
const SortableLeadCard = ({ lead, onDelete, onEdit, onView }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: lead.id });
  
  const [leadScore, setLeadScore] = React.useState(null);

  React.useEffect(() => {
    // Fetch lead score
    const fetchScore = async () => {
      try {
        const response = await api.get(`/leads/${lead.id}/score`);
        setLeadScore(response.data);
      } catch (error) {
        console.error('Error fetching lead score:', error);
      }
    };
    fetchScore();
  }, [lead.id]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  
  const getScoreColor = (score) => {
    if (score >= 75) return 'bg-green-100 text-green-800 border-green-300';
    if (score >= 50) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-red-100 text-red-800 border-red-300';
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card 
        className="mb-3 cursor-move hover:shadow-lg transition-all duration-200 border-l-4 border-l-blue-500 bg-white"
        {...attributes}
        {...listeners}
      >
        <CardContent className="p-4">
          <div className="flex justify-between items-start mb-2">
            <h4 className="font-semibold text-sm text-slate-900 line-clamp-2 flex-1">
              {lead.potential_project_name}
            </h4>
            <div className="flex gap-1 ml-2">
              {leadScore && (
                <Badge className={`text-xs font-bold border ${getScoreColor(leadScore.score)}`}>
                  {leadScore.score}
                </Badge>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 hover:bg-blue-50"
                onClick={(e) => {
                  e.stopPropagation();
                  onView(lead);
                }}
              >
                <Eye className="h-3.5 w-3.5 text-blue-600" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 hover:bg-red-50"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(lead);
                }}
              >
                <Trash2 className="h-3.5 w-3.5 text-red-600" />
              </Button>
            </div>
          </div>
          
          {lead.location && (
            <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-2">
              <MapPin className="h-3 w-3" />
              <span className="truncate">{lead.location}</span>
            </div>
          )}
          
          <div className="flex items-center gap-3 mb-3">
            {lead.est_units && (
              <div className="text-xs">
                <span className="font-medium text-slate-700">{lead.est_units}</span>
                <span className="text-slate-500 ml-1">units</span>
              </div>
            )}
            {lead.est_sqft && (
              <div className="text-xs">
                <span className="font-medium text-slate-700">{lead.est_sqft.toLocaleString()}</span>
                <span className="text-slate-500 ml-1">sqft</span>
              </div>
            )}
          </div>
          
          {lead.est_value && (
            <div className="flex items-center gap-1.5 mb-3 bg-green-50 px-2 py-1.5 rounded">
              <DollarSign className="h-4 w-4 text-green-600" />
              <span className="font-semibold text-green-700">
                ${lead.est_value.toLocaleString()}
              </span>
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <Badge variant="outline" className="text-xs bg-slate-50">
              {lead.source}
            </Badge>
            {lead.company_id && (
              <Building2 className="h-3.5 w-3.5 text-slate-400" />
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Deleted Lead Card Component (non-draggable)
const DeletedLeadCard = ({ lead, onRestore }) => {
  const getDaysDeleted = (deletedAt) => {
    if (!deletedAt) return '';
    const days = Math.floor((new Date() - new Date(deletedAt)) / (1000 * 60 * 60 * 24));
    const remaining = 15 - days;
    return { days, remaining };
  };
  
  const deletionInfo = getDaysDeleted(lead.deleted_at);
  
  return (
    <Card className="mb-3 bg-slate-50 opacity-75 border-dashed">
      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-2">
          <h4 className="font-semibold text-sm text-slate-700 line-clamp-2 flex-1">
            {lead.potential_project_name}
          </h4>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 hover:bg-green-50"
            onClick={() => onRestore(lead)}
          >
            <RotateCcw className="h-3.5 w-3.5 text-green-600" />
          </Button>
        </div>
        
        {lead.location && (
          <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-2">
            <MapPin className="h-3 w-3" />
            <span className="truncate">{lead.location}</span>
          </div>
        )}
        
        {lead.est_value && (
          <div className="flex items-center gap-1.5 text-xs text-slate-600 mb-2">
            <DollarSign className="h-3 w-3" />
            <span>${lead.est_value.toLocaleString()}</span>
          </div>
        )}
        
        <div className="mt-2 flex items-center gap-2">
          <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
            Deleted
          </Badge>
          {deletionInfo.days !== undefined && (
            <span className="text-xs text-slate-500">
              {deletionInfo.days}d ago • {deletionInfo.remaining > 0 ? (
                <span className="text-orange-600">{deletionInfo.remaining}d to restore</span>
              ) : (
                <span className="text-red-600">Will be deleted soon</span>
              )}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const LeadsPage = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [viewMode, setViewMode] = useState('active'); // 'active' or 'deleted'

  const [formData, setFormData] = useState({
    potential_project_name: '',
    company_id: '',
    contact_id: '',
    source: 'Referral',
    location: '',
    est_units: '',
    est_sqft: '',
    est_value: '',
    notes: '',
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    fetchLeads();
    fetchStats();
    fetchCompanies();
    fetchContacts();
  }, [viewMode]);

  const fetchLeads = async () => {
    try {
      const includeDeleted = viewMode === 'deleted';
      const response = await api.get('/leads', { params: { include_deleted: includeDeleted } });
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to fetch leads');
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await api.get('/contacts');
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/crm/stats/summary');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/leads', {
        ...formData,
        est_units: formData.est_units ? parseInt(formData.est_units) : null,
        est_sqft: formData.est_sqft ? parseInt(formData.est_sqft) : null,
        est_value: formData.est_value ? parseFloat(formData.est_value) : null,
      });
      toast.success('Lead created successfully');
      setDialogOpen(false);
      fetchLeads();
      fetchStats();
      resetForm();
    } catch (error) {
      console.error('Error creating lead:', error);
      toast.error('Failed to create lead');
    }
  };

  const handleView = (lead) => {
    navigate(`/leads/${lead.id}`);
  };

  const handleDelete = (lead) => {
    setLeadToDelete(lead);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/leads/${leadToDelete.id}`);
      toast.success('Lead deleted successfully');
      setDeleteDialogOpen(false);
      setLeadToDelete(null);
      fetchLeads();
      fetchStats();
    } catch (error) {
      console.error('Error deleting lead:', error);
      toast.error('Failed to delete lead');
    }
  };

  const handleRestore = async (lead) => {
    try {
      await api.post(`/leads/${lead.id}/restore`);
      toast.success('Lead restored successfully');
      fetchLeads();
      fetchStats();
    } catch (error) {
      console.error('Error restoring lead:', error);
      toast.error('Failed to restore lead');
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeLeadId = active.id;
    const newStatus = over.id;

    // Find the lead being dragged
    const lead = leads.find(l => l.id === activeLeadId);
    
    if (lead && lead.status !== newStatus) {
      // Optimistically update UI
      setLeads(prevLeads =>
        prevLeads.map(l =>
          l.id === activeLeadId ? { ...l, status: newStatus } : l
        )
      );

      try {
        await api.put(`/leads/${activeLeadId}`, { status: newStatus });
        toast.success('Lead status updated');
        fetchStats();
      } catch (error) {
        console.error('Error updating lead status:', error);
        toast.error('Failed to update lead status');
        // Revert on error
        fetchLeads();
      }
    }
  };

  const resetForm = () => {
    setFormData({
      potential_project_name: '',
      company_id: '',
      contact_id: '',
      source: 'Referral',
      location: '',
      est_units: '',
      est_sqft: '',
      est_value: '',
      notes: '',
    });
  };

  const statusColumns = [
    { status: 'New', color: 'bg-gray-100 text-gray-800', borderColor: 'border-gray-300' },
    { status: 'Contacted', color: 'bg-blue-100 text-blue-800', borderColor: 'border-blue-300' },
    { status: 'Qualified', color: 'bg-purple-100 text-purple-800', borderColor: 'border-purple-300' },
    { status: 'QuoteSent', color: 'bg-yellow-100 text-yellow-800', borderColor: 'border-yellow-300', label: 'Quote Sent' },
    { status: 'Negotiation', color: 'bg-orange-100 text-orange-800', borderColor: 'border-orange-300' },
    { status: 'Won', color: 'bg-green-100 text-green-800', borderColor: 'border-green-300' },
    { status: 'Lost', color: 'bg-red-100 text-red-800', borderColor: 'border-red-300' },
  ];

  const getLeadsByStatus = (status) => {
    return leads.filter(lead => 
      lead.status === status && 
      (viewMode === 'deleted' ? lead.soft_delete : !lead.soft_delete)
    );
  };

  const getDeletedLeads = () => {
    return leads.filter(lead => lead.soft_delete);
  };

  const activeLead = activeId ? leads.find(l => l.id === activeId) : null;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Leads Pipeline</h1>
          <p className="text-slate-600 mt-1">Track your sales opportunities</p>
        </div>
        <div className="flex gap-3">
          <div className="flex gap-2">
            <Button 
              variant={viewMode === 'active' ? 'default' : 'outline'} 
              onClick={() => setViewMode('active')}
            >
              Active Leads
            </Button>
            <Button 
              variant={viewMode === 'deleted' ? 'default' : 'outline'} 
              onClick={() => setViewMode('deleted')}
              className="relative"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Deleted
              {getDeletedLeads().length > 0 && (
                <Badge className="ml-2 bg-red-500">{getDeletedLeads().length}</Badge>
              )}
            </Button>
          </div>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Lead
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total Leads</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900">{stats.total_leads || 0}</div>
            <p className="text-xs text-slate-500 mt-1">Active opportunities</p>
          </CardContent>
        </Card>
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Qualified</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">{stats.qualified_leads || 0}</div>
            <p className="text-xs text-slate-500 mt-1">Ready for quotes</p>
          </CardContent>
        </Card>
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Won</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{stats.won_leads || 0}</div>
            <p className="text-xs text-slate-500 mt-1">Converted to projects</p>
          </CardContent>
        </Card>
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Est. Value</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900">${(stats.total_est_value || 0).toLocaleString()}</div>
            <p className="text-xs text-slate-500 mt-1">Pipeline value</p>
          </CardContent>
        </Card>
      </div>

      {/* Kanban Board or Deleted View */}
      {viewMode === 'active' ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {statusColumns.map((column) => {
              const columnLeads = getLeadsByStatus(column.status);
              return (
                <SortableContext
                  key={column.status}
                  id={column.status}
                  items={columnLeads.map(l => l.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <Card className={`min-h-[500px] border-t-4 ${column.borderColor} shadow-sm hover:shadow-md transition-shadow`}>
                    <CardHeader className="pb-3 bg-slate-50">
                      <CardTitle className="text-sm font-semibold flex items-center justify-between">
                        <Badge className={column.color}>
                          {column.label || column.status}
                        </Badge>
                        <span className="text-slate-500 font-normal ml-2">({columnLeads.length})</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                      {columnLeads.map((lead) => (
                        <SortableLeadCard
                          key={lead.id}
                          lead={lead}
                          onView={handleView}
                          onDelete={handleDelete}
                        />
                      ))}
                      {columnLeads.length === 0 && (
                        <div className="text-center py-8 text-slate-400 text-sm">
                          No leads
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </SortableContext>
              );
            })}
          </div>
          <DragOverlay>
            {activeLead ? (
              <Card className="cursor-grabbing opacity-90 shadow-2xl border-2 border-blue-500">
                <CardContent className="p-4">
                  <h4 className="font-semibold text-sm">{activeLead.potential_project_name}</h4>
                </CardContent>
              </Card>
            ) : null}
          </DragOverlay>
        </DndContext>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-600" />
              Deleted Leads
            </CardTitle>
          </CardHeader>
          <CardContent>
            {getDeletedLeads().length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Trash2 className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p>No deleted leads</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {getDeletedLeads().map((lead) => (
                  <DeletedLeadCard
                    key={lead.id}
                    lead={lead}
                    onRestore={handleRestore}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create Lead Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Lead</DialogTitle>
            <DialogDescription>Create a new sales opportunity</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="potential_project_name">Project Name *</Label>
                <Input
                  id="potential_project_name"
                  value={formData.potential_project_name}
                  onChange={(e) => setFormData({ ...formData, potential_project_name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="source">Source *</Label>
                <Select
                  value={formData.source}
                  onValueChange={(value) => setFormData({ ...formData, source: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Referral">Referral</SelectItem>
                    <SelectItem value="ColdEmail">Cold Email</SelectItem>
                    <SelectItem value="Website">Website</SelectItem>
                    <SelectItem value="WalkIn">Walk-In</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company_id">Company</Label>
                <Select
                  value={formData.company_id || undefined}
                  onValueChange={(value) => setFormData({ ...formData, company_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select company (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_id">Contact</Label>
                <Select
                  value={formData.contact_id || undefined}
                  onValueChange={(value) => setFormData({ ...formData, contact_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select contact (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {contacts.map((contact) => (
                      <SelectItem key={contact.id} value={contact.id}>
                        {contact.first_name} {contact.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="City, State or full address"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="est_units">Est. Units</Label>
                <Input
                  id="est_units"
                  type="number"
                  value={formData.est_units}
                  onChange={(e) => setFormData({ ...formData, est_units: e.target.value })}
                  placeholder="Number of units"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="est_sqft">Est. Sq Ft</Label>
                <Input
                  id="est_sqft"
                  type="number"
                  value={formData.est_sqft}
                  onChange={(e) => setFormData({ ...formData, est_sqft: e.target.value })}
                  placeholder="Square footage"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="est_value">Est. Value ($)</Label>
                <Input
                  id="est_value"
                  type="number"
                  step="0.01"
                  value={formData.est_value}
                  onChange={(e) => setFormData({ ...formData, est_value: e.target.value })}
                  placeholder="Estimated value"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Input
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional information"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                Create Lead
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Lead?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{leadToDelete?.potential_project_name}"? 
              You can restore it later from the Deleted tab.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LeadsPage;
